import React from 'react';
import { BsChevronDown, BsChevronUp } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import {
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { CypressIds } from '../../constants/cypressIds';
import LoadingSpinner from '../Spinner';

const StatusButton = ({
  options,
  disabled,
  isLoading,
  metadata,
  duskSelector = undefined,
  duskId = undefined,
  isFetching = undefined,
  cypressId
}) => {
  const dispatch = useDispatch();

  if (isLoading || !options) {
    return (
      <Flex alignItems="center" height="32px" justifyContent="center" minW="170px">
        <LoadingSpinner isInline spinnerSize="md" />
      </Flex>
    );
  }

  const {
    arrowColor,
    backgroundColor,
    fontColor,
    hoverBackgroundColor,
    statusText,
    transitions
  } = options;

  const isDisabled = disabled || !transitions?.length;

  return (
    <Menu>
      {({ isOpen }) => (
        <>
          <MenuButton
            _active={{ background: [backgroundColor] }}
            _disabled={{ opacity: '0.6', pointerEvents: 'none', cursor: 'not-allowed' }}
            _focus={{ outline: '0px', background: [hoverBackgroundColor] }}
            _hover={{ background: [hoverBackgroundColor] }}
            as={Button}
            bg={backgroundColor}
            border="1px solid"
            borderRadius="100px"
            color={fontColor}
            data-cy={cypressId}
            data-dusk-value={statusText}
            disabled={isDisabled}
            dusk={duskSelector}
            isLoading={isFetching}
            maxH="32px"
            minW="170px"
            rightIcon={isDisabled ? '' : <Icon as={isOpen ? BsChevronUp : BsChevronDown} color={arrowColor} />}
            textStyle="button"
          >
            {statusText}
          </MenuButton>
          <Portal appendToParentPortal={false}>
            <MenuList minW="170px" mt={0} zIndex="popover">
              {transitions.map(transition => {
                const value = transition.status;
                return (
                  <MenuItem
                    data-analytics-id={transition?.dataAnalyticsId}
                    data-cy={CypressIds.statusButtonValue(cypressId, transition.statusText)}
                    dusk={`${duskSelector}_status-${transition.statusText}`}
                    isDisabled={statusText === transition.statusText}
                    key={value}
                    value={value}
                    onClick={() => transition.handler(value, dispatch, metadata)}
                  >
                    <Icon as={transition.icon} color={transition.iconColor} mr="15px" />
                    <span>{transition.actionText || transition.statusText}</span>
                  </MenuItem>
                );
              })}
            </MenuList>
          </Portal>
        </>
      )}
    </Menu>
  );
};

StatusButton.propTypes = {
  options: PropTypes.shape({
    arrowColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    fontColor: PropTypes.string,
    hoverBackgroundColor: PropTypes.string,
    statusText: PropTypes.string,
    transitions: PropTypes.arrayOf(PropTypes.shape({
      actionText: PropTypes.string,
      statusText: PropTypes.string,
      handler: PropTypes.func,
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
      iconColor: PropTypes.string
    }))
  }).isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  metadata: PropTypes.shape({}),
  duskSelector: PropTypes.string,
  isFetching: PropTypes.bool
};

export default StatusButton;
